import {Checkbox, Col, Form, Input, InputNumber, Row, Select, Slider} from 'antd';
import React from 'react';

import {CommonErrors} from '../../../constants/Common';
import {LCI_FIELD_HINTS} from '../constants/lciFieldHints';
import {
  PCF_COL_GUTTER_CLASS,
  PCF_COL_SPAN_HALF,
  rowGutter
} from '../constants/pcfFormLayout';
import {
  BOM_OPTIONS,
  SHIP_TO_OPTIONS,
  TRANSPORT_MODES,
  TRUCK_LOAD_FACTORS
} from '../constants/pcfOptions';
import {LogisticsKeys, LogisticsLabels} from '../PCFKeyLabels';

import {FormItemLabel} from './FormItemLabel';
import {ltlLoadPercentRules, positiveUnitsRule} from './lciValidation';
import PCFAddButton from './PCFAddButton';
import RemovableCard from './RemovableCard';

const blankItem = (variant: 'inbound' | 'outbound') => ({
  material: '',
  supplierCity: '',
  destinationCity: '',
  mode: 'road_truck_diesel',
  distance: '',
  annualQuantity: '',
  loadFactor: 'ftl',
  ltlLoadPercent: 50,
  mixedModeSplit: {},
  ef: '',
  returnJourney: false,
  ...(variant === 'outbound' ? {} : {})
});

export const transportDefaults = {
  shipTo: '',
  items: [blankItem('inbound')]
};

export const outboundTransportDefaults = {
  shipTo: 'customers',
  items: [blankItem('outbound')]
};

function LogisticsFields({
  name,
  variant,
  listBasePath
}: {
  name: number;
  variant: 'inbound' | 'outbound';
  listBasePath: ['a2'] | ['a4'];
}) {
  const form = Form.useFormInstance();
  const loadFactor = Form.useWatch(
    [...listBasePath, LogisticsKeys.ITEMS, name, LogisticsKeys.LOAD_FACTOR],
    form
  );
  const mode = Form.useWatch(
    [...listBasePath, LogisticsKeys.ITEMS, name, LogisticsKeys.MODE],
    form
  );

  return (
    <Row gutter={rowGutter}>
      {variant === 'inbound' && (
        <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
          <Form.Item
            name={[name, LogisticsKeys.MATERIAL]}
            label={LogisticsLabels.MATERIAL}
            rules={[
              {
                required: true,
                message: CommonErrors.mandatory(LogisticsLabels.MATERIAL)
              }
            ]}
            validateFirst
            hasFeedback
          >
            <Select
              className="select"
              placeholder="Select from BOM..."
              options={BOM_OPTIONS}
            />
          </Form.Item>
        </Col>
      )}
      <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
        <Form.Item
          name={[
            name,
            variant === 'inbound'
              ? LogisticsKeys.SUPPLIER_CITY
              : LogisticsKeys.DESTINATION_CITY
          ]}
          required
          label={
            variant === 'inbound' ? (
              <FormItemLabel hint={LCI_FIELD_HINTS.supplierCity}>
                {LogisticsLabels.SUPPLIER_CITY}
              </FormItemLabel>
            ) : (
              <FormItemLabel hint={LCI_FIELD_HINTS.destinationCity}>
                {LogisticsLabels.DESTINATION_CITY}
              </FormItemLabel>
            )
          }
          rules={[
            {
              required: true,
              whitespace: true,
              message: CommonErrors.mandatory(
                variant === 'inbound'
                  ? LogisticsLabels.SUPPLIER_CITY
                  : LogisticsLabels.DESTINATION_CITY
              )
            }
          ]}
          validateFirst
          hasFeedback
        >
          <Input className="input" placeholder="e.g. Surat, Gujarat" />
        </Form.Item>
      </Col>
      <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
        <Form.Item
          name={[name, LogisticsKeys.MODE]}
          label={LogisticsLabels.MODE}
          rules={[
            {
              required: true,
              message: CommonErrors.mandatory(LogisticsLabels.MODE)
            }
          ]}
          validateFirst
          hasFeedback
        >
          <Select className="select" options={TRANSPORT_MODES} />
        </Form.Item>
      </Col>
      <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
        <Form.Item
          name={[name, LogisticsKeys.DISTANCE]}
          required
          label={LogisticsLabels.DISTANCE}
          rules={[positiveUnitsRule(LogisticsLabels.DISTANCE)]}
          validateFirst
          hasFeedback
        >
          <InputNumber
            controls={false}
            className="input"
            placeholder="Distance auto-filled or manual"
            style={{width: '100%'}}
            min={0}
          />
        </Form.Item>
      </Col>
      <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
        <Form.Item
          name={[name, LogisticsKeys.ANNUAL_QUANTITY]}
          required
          label={
            <FormItemLabel hint={LCI_FIELD_HINTS.annualQuantity}>
              {LogisticsLabels.ANNUAL_QUANTITY}
            </FormItemLabel>
          }
          rules={[positiveUnitsRule(LogisticsLabels.ANNUAL_QUANTITY)]}
          validateFirst
          hasFeedback
        >
          <InputNumber
            controls={false}
            className="input"
            style={{width: '100%'}}
            min={0}
          />
        </Form.Item>
      </Col>
      <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
        <Form.Item
          name={[name, LogisticsKeys.LOAD_FACTOR]}
          label={LogisticsLabels.LOAD_FACTOR}
          rules={[
            {
              required: true,
              message: CommonErrors.mandatory(LogisticsLabels.LOAD_FACTOR)
            }
          ]}
          validateFirst
          hasFeedback
        >
          <Select className="select" options={TRUCK_LOAD_FACTORS} />
        </Form.Item>
      </Col>
      {loadFactor === 'ltl' && (
        <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
          <Form.Item
            name={[name, LogisticsKeys.LTL_LOAD_PERCENT]}
            required={loadFactor === 'ltl'}
            label={LogisticsLabels.LTL_LOAD_PERCENT}
            rules={ltlLoadPercentRules(() => loadFactor ?? '')}
            validateFirst
            hasFeedback
          >
            <Slider min={1} max={100} />
          </Form.Item>
        </Col>
      )}
      {mode === 'mixed' && (
        <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
          <Form.Item
            name={[name, LogisticsKeys.MIXED_MODE_SPLIT, 'road']}
            label={`${LogisticsLabels.MIXED_MODE_SPLIT} — Road (%)`}
          >
            <InputNumber
              controls={false}
              className="input"
              style={{width: '100%'}}
              min={0}
              max={100}
            />
          </Form.Item>
        </Col>
      )}
      <Col className={PCF_COL_GUTTER_CLASS} span={PCF_COL_SPAN_HALF}>
        <Form.Item name={[name, LogisticsKeys.EF]} label={LogisticsLabels.EF}>
          <InputNumber
            controls={false}
            className="input"
            style={{width: '100%'}}
            min={0}
          />
        </Form.Item>
      </Col>
      <Col className={PCF_COL_GUTTER_CLASS} span={24}>
        <Form.Item
          name={[name, LogisticsKeys.RETURN_JOURNEY]}
          valuePropName="checked"
        >
          <Checkbox>{LogisticsLabels.RETURN_JOURNEY}</Checkbox>
        </Form.Item>
      </Col>
    </Row>
  );
}

const TransportSection: React.FC<{
  variant: 'inbound' | 'outbound';
  sectionTitle: string;
  addLabel: string;
  cardTitlePrefix?: string;
  listBasePath: ['a2'] | ['a4'];
}> = ({
  variant,
  sectionTitle,
  addLabel,
  cardTitlePrefix = 'Supplier Logistics',
  listBasePath
}) => {
  return (
    <div className="lci-section">
      <div className="lci-section-header">
        <h3 className="lci-section-title">{sectionTitle}</h3>
      </div>

      {variant === 'outbound' && (
        <Row gutter={rowGutter}>
          <Col className={PCF_COL_GUTTER_CLASS} span={24}>
            <Form.Item
              name={[...listBasePath, LogisticsKeys.SHIP_TO]}
              label={LogisticsLabels.SHIP_TO}
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(LogisticsLabels.SHIP_TO)
                }
              ]}
              validateFirst
              hasFeedback
            >
              <Select className="select" options={SHIP_TO_OPTIONS} />
            </Form.Item>
          </Col>
        </Row>
      )}

      <Form.List
        name={[...listBasePath, LogisticsKeys.ITEMS]}
        rules={[
          {
            validator: async (_, items) => {
              if (!items?.length) {
                return await Promise.reject(
                  new Error('Add at least one transport leg')
                );
              }

              await Promise.resolve();
            }
          }
        ]}
      >
        {(fields, {add, remove}, {errors: listErrors}) => (
          <>
            {listErrors?.length > 0 && (
              <div className="field-error">{listErrors[0]}</div>
            )}
            <div className="lci-section-header" style={{marginTop: 0}}>
              <PCFAddButton
                onClick={() => {
                  add(blankItem(variant));
                }}
              >
                {addLabel}
              </PCFAddButton>
            </div>
            <div className="lci-cards-list">
              {fields.map(({key, name}, idx) => (
                <RemovableCard
                  key={key}
                  title={`${cardTitlePrefix} #${idx + 1}`}
                  onRemove={() => {
                    remove(name);
                  }}
                  showRemove={idx > 0}
                >
                  <LogisticsFields
                    name={name}
                    variant={variant}
                    listBasePath={listBasePath}
                  />
                </RemovableCard>
              ))}
            </div>
          </>
        )}
      </Form.List>
    </div>
  );
};

export default TransportSection;
